import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  baseMoraDe,
  metodoPrelacionDe,
  fmtFecha,
  fmtFechaCorta,
  fmtMXN,
  r2,
  type FilaReal,
  type TramoAplicado,
} from "@/lib/operativo";
import { useOperativo } from "@/lib/operativo-store";

interface Item {
  key: string;
  periodo: number;
  tramo: TramoAplicado;
  mensualidadVencida: number;
  totalAPagar: number;
}

/** Aplana los pagos capturados para mostrar la cascada de aplicación de cada uno. */
function items(filas: FilaReal[]): Item[] {
  const out: Item[] = [];
  for (const f of filas) {
    f.tramos.forEach((t, idx) => {
      const propio = t.aplicaciones.find((a) => a.propio);
      const pendCuota = propio
        ? r2(
            propio.renglones
              .filter(
                (r) =>
                  r.concepto === "Interés ordinario" || r.concepto === "Capital (cuota del mes)",
              )
              .reduce((a, r) => a + r.adeudo, 0),
          )
        : 0;
      out.push({
        key: `${f.periodo}-${idx}`,
        periodo: f.periodo,
        tramo: t,
        mensualidadVencida: pendCuota,
        totalAPagar: r2(pendCuota + t.mora + t.ivaMora + t.pena + t.ivaPena),
      });
    });
  }
  return out;
}

export function DesgloseAplicacion() {
  const { real, solicitud } = useOperativo();
  const lista = items(real.filas);
  const baseMora = baseMoraDe(solicitud.params);
  const metodoPrel = metodoPrelacionDe(solicitud.params);

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-navy">Desglose de aplicación</h3>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Cascada por antigüedad: cada pago cubre primero los períodos vencidos más antiguos y
            sólo al final abona capital extra.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {metodoPrel === "Secuencial estricto"
              ? "Prelación secuencial estricta: se agota cada nivel completo antes de pasar al siguiente (IVA pena → pena → IVA mora → mora → IVA interés → interés → capital)."
              : "Prelación con reparto proporcional 1:0.16 entre cada IVA y su concepto base."}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Interés moratorio = (Capital vencido + Interés ordinario vencido) × Tasa moratoria anual
            / 360 × Días de atraso
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline">Base de mora: {baseMora}</Badge>
          <Badge variant="secondary">Prelación: {metodoPrel}</Badge>
        </div>
      </div>

      {lista.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          Captura un pago para ver su desglose de aplicación.
        </p>
      ) : (
        <div className="mt-4 space-y-5">
          {lista.map((d) => (
            <div key={d.key} className="rounded-md border border-border">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 border-b border-border bg-surface px-4 py-3 text-xs">
                <span className="font-semibold text-navy">
                  Pago del período {d.periodo} · {fmtFecha(d.tramo.fecha)}
                </span>
                <span>
                  Pago recibido: <strong className="num">{fmtMXN(d.tramo.monto)}</strong>
                </span>
                <span>
                  Días de mora del período: <strong className="num">{d.tramo.diasAtraso}</strong>{" "}
                  <span className="text-muted-foreground">
                    (desde {fmtFechaCorta(d.tramo.desde)})
                  </span>
                </span>
                <span>
                  Saldo vencido no pagado:{" "}
                  <strong className="num">{fmtMXN(d.mensualidadVencida)}</strong>
                </span>
                <span>
                  Monto total a pagar: <strong className="num">{fmtMXN(d.totalAPagar)}</strong>
                </span>
                {d.tramo.referencia && (
                  <span className="text-muted-foreground">Ref. {d.tramo.referencia}</span>
                )}
              </div>

              {d.tramo.aplicaciones.map((a) => (
                <div key={`${d.key}-${a.periodo}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 px-4 py-2 text-xs">
                    <span
                      className={
                        a.propio ? "font-medium text-navy" : "font-medium text-destructive"
                      }
                    >
                      {a.propio
                        ? `Aplicado al Período ${a.periodo} (exigible)`
                        : `Aplicado a Período ${a.periodo} (vencido)`}
                    </span>
                    <span className="text-muted-foreground">
                      Mora del tramo: <span className="num">{a.dias}</span> día(s) desde{" "}
                      {fmtFechaCorta(a.desde)} · Aplicado:{" "}
                      <strong className="num">{fmtMXN(a.total)}</strong>
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="px-4 py-2 text-left font-medium">#</th>
                          <th className="px-4 py-2 text-left font-medium">Concepto</th>
                          <th className="px-4 py-2 text-right font-medium">Adeudo</th>
                          <th className="px-4 py-2 text-right font-medium">Monto aplicado</th>
                          <th className="px-4 py-2 text-right font-medium">Saldo restante</th>
                        </tr>
                      </thead>
                      <tbody>
                        {a.renglones.map((r, i) => (
                          <tr
                            key={r.concepto}
                            className={
                              r.aplicado > 0
                                ? "border-b border-border/60"
                                : "border-b border-border/60 text-muted-foreground"
                            }
                          >
                            <td className="px-4 py-2 num">{i + 1}</td>
                            <td className="px-4 py-2">{r.concepto}</td>
                            <td className="px-4 py-2 text-right num">{fmtMXN(r.adeudo)}</td>
                            <td className="px-4 py-2 text-right num font-medium">
                              {fmtMXN(r.aplicado)}
                            </td>
                            <td className="px-4 py-2 text-right num">{fmtMXN(r.restante)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {(d.tramo.aplCapitalExtra > 0 || d.tramo.sobrepago > 0) && (
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 text-sm">
                  {d.tramo.aplCapitalExtra > 0 && (
                    <span className="text-navy">
                      Abono extra a Capital:{" "}
                      <strong className="num">{fmtMXN(d.tramo.aplCapitalExtra)}</strong>
                    </span>
                  )}
                  {d.tramo.sobrepago > 0 && (
                    <span className="text-emerald">
                      Sobrepago (a favor del cliente):{" "}
                      <strong className="num">{fmtMXN(d.tramo.sobrepago)}</strong>
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
